import React from 'react';

import { Button, Drawer, Flex, Spin, Tooltip, Typography } from 'antd';

import './DuplicateShipmentDrawer.css';
import { DuplicateShipmentOutboundSection } from './DuplicateShipmentOutboundSection';
import { DuplicateShipmentReturnSection } from './DuplicateShipmentReturnSection';
import { EditableCard } from './EditableCard';
import { useDuplicateShipmentFormView } from '../hooks/useDuplicateShipmentFormView';
import { useDuplicateSnapshotHandlers } from '../hooks/useDuplicateSnapshotHandlers';
import { IModifiedShipment } from '../models/shipment';
import { SearchBox } from '@/features/boxes/components/SearchBox';
import { SearchPartner } from '@/features/lp-village';

interface IDuplicateShipmentDrawerProps {
  open: boolean;
  onClose: () => void;
  shipment: IModifiedShipment;
  originalServiceLevelId: string;
}

export const DuplicateShipmentDrawer = ({
  open,
  onClose,
  shipment: sourceShipment,
  originalServiceLevelId,
}: IDuplicateShipmentDrawerProps) => {
  const {
    formik,
    initialData,
    carrierOptions,
    serviceTypeEnabled,
    serviceTypeOptions,
    showReturnSection,
    partnerName,
    outboundLabelUrl,
    returnLabelUrl,
    file,
    setFile,
    returnFile,
    setReturnFile,
    getAddressFormProps,
    getAddressSummary,
    hasAddressError,
    handlePartnerChange,
    handleServiceLevelChange,
  } = useDuplicateShipmentFormView(
    sourceShipment,
    originalServiceLevelId,
    onClose
  );

  const { values, setFieldValue, setValues, setTouched } = formik;

  const {
    handleEditingChange,
    hasEditingCards,
    hasErrorsInFields,
    getAddressFields,
    partnershipHandlers,
    boxHandlers,
    parcelHandlers,
    senderHandlers,
    recipientHandlers,
    returnCarrierHandlers,
    returnSenderHandlers,
    returnRecipientHandlers,
  } = useDuplicateSnapshotHandlers({
    values,
    errors: formik.errors,
    touched: formik.touched,
    setValues,
    setTouched,
    handlePartnerChange,
    setFieldValue,
    file,
    returnFile,
    setFile,
    setReturnFile,
  });

  const submitDisabled =
    !values.partnerId || formik.isSubmitting || hasEditingCards;

  return (
    <Drawer
      title="Duplicate Shipment"
      placement="right"
      open={open}
      onClose={onClose}
      width="80%"
      styles={{
        wrapper: {
          marginTop: '70px',
          borderTopLeftRadius: '10px',
          transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
        },
      }}
      footer={
        <Flex justify="flex-end" gap="small">
          <Button data-testid="duplicate-cancel-button" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip
            title={
              hasEditingCards
                ? 'Please click "Done" on all sections before submitting'
                : ''
            }
          >
            <Button
              data-testid="duplicate-submit-button"
              type="primary"
              onClick={() => formik.submitForm()}
              loading={formik.isSubmitting}
              disabled={submitDisabled}
            >
              Create Shipment
            </Button>
          </Tooltip>
        </Flex>
      }
    >
      <Spin spinning={formik.isSubmitting} tip="Creating shipment…">
        <Flex vertical gap="0.25rem" style={{ marginBottom: '1.5rem' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Summary of the shipping
          </Typography.Title>
          <Typography.Text type="secondary">
            Check and validate your shipping information.
          </Typography.Text>
        </Flex>

        <Flex vertical gap="0.75rem">
          {/* Partnership */}
          <EditableCard
            title="Partnership"
            defaultEditing={!values.partnerId}
            onEditingChange={handleEditingChange}
            onEditStart={partnershipHandlers.onEditStart}
            onCancel={partnershipHandlers.onCancel}
            summary={
              <Typography.Text type={partnerName ? undefined : 'secondary'}>
                {partnerName || values.partnerId || '—'}
              </Typography.Text>
            }
          >
            <SearchPartner
              searchMultiple
              partnerId={values.partnerId}
              setPartnerId={handlePartnerChange}
              selectedPartner={values.selectedPartner}
              setSelectedPartner={(p) => setFieldValue('selectedPartner', p)}
              setServiceLevel={handleServiceLevelChange}
            />
          </EditableCard>

          {/* Box Assignment */}
          <EditableCard
            title="Box Assignment (Optional)"
            onEditingChange={handleEditingChange}
            onEditStart={boxHandlers.onEditStart}
            onCancel={boxHandlers.onCancel}
            summary={
              <Typography.Text type={values.boxLabel ? undefined : 'secondary'}>
                {values.boxLabel || 'No box assigned'}
              </Typography.Text>
            }
          >
            <SearchBox
              boxId={values.boxId || ''}
              setBoxId={(id) => setFieldValue('boxId', id)}
              selectedBox={values.boxLabel}
              setSelectedBox={(label) => setFieldValue('boxLabel', label)}
            />
          </EditableCard>

          {/* Outgoing trip */}
          <DuplicateShipmentOutboundSection
            values={{
              carrier: values.carrier,
              reference: values.reference,
              carrierTrackingCode: values.carrierTrackingCode,
              weightInGrams: values.weightInGrams,
              serviceType: values.serviceType,
              senderEmail: values.senderEmail,
              recipientEmail: values.recipientEmail,
            }}
            setFieldValue={setFieldValue}
            carrierOptions={carrierOptions}
            serviceTypeEnabled={serviceTypeEnabled}
            serviceTypeOptions={serviceTypeOptions}
            outboundLabelUrl={outboundLabelUrl}
            file={file}
            setFile={setFile}
            parcelHandlers={parcelHandlers}
            senderHandlers={senderHandlers}
            recipientHandlers={recipientHandlers}
            handleEditingChange={handleEditingChange}
            hasErrorsInFields={hasErrorsInFields}
            getAddressFields={getAddressFields}
            getAddressFormProps={getAddressFormProps}
            getAddressSummary={getAddressSummary}
            hasAddressError={hasAddressError}
          />

          {/* Return Shipment */}
          {showReturnSection && (
            <DuplicateShipmentReturnSection
              values={{
                returnCarrier: values.returnCarrier,
                returnReference: values.returnReference,
                returnCarrierTrackingCode: values.returnCarrierTrackingCode,
                weightInGrams: values.weightInGrams,
                addReturn: values.addReturn,
                returnSenderEmail: values.returnSenderEmail,
                returnRecipientEmail: values.returnRecipientEmail,
              }}
              setFieldValue={setFieldValue}
              initialHasReturn={initialData.hasReturn}
              returnLabelUrl={returnLabelUrl}
              returnFile={returnFile}
              setReturnFile={setReturnFile}
              carrierOptions={carrierOptions}
              returnCarrierHandlers={returnCarrierHandlers}
              returnSenderHandlers={returnSenderHandlers}
              returnRecipientHandlers={returnRecipientHandlers}
              handleEditingChange={handleEditingChange}
              hasErrorsInFields={hasErrorsInFields}
              getAddressFields={getAddressFields}
              getAddressFormProps={getAddressFormProps}
              getAddressSummary={getAddressSummary}
              hasAddressError={hasAddressError}
            />
          )}
        </Flex>
      </Spin>
    </Drawer>
  );
};
